// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useNavigate } from '@tanstack/react-router';

// material ui icons
import EnergySavingsLeafRoundedIcon from '@mui/icons-material/EnergySavingsLeafRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

// material ui
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { DataGridPremium } from '@mui/x-data-grid-premium';

// api
import type { GetEnergyGroupDto } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import PercentageTrendChip from '@/components/ui/chip/PercentageTrendChip';
import EnergyGroupTypeIcon from '@/components/ui/icon/EnergyGroupTypeIcon';

// feature-components
import EnergyXplorerCard from '@/features/energyxplorer/components/sales-cards/EnergyXplorerCard';

// components
import { useDatagridLocaleText } from '@/hooks/useDatagridLocaleText';
import { useHasService } from '@/hooks/useHasService';
import { formatNumber } from '@/utils/formatters';

// relative
import { useEnergyDeepDiveTrend } from '../../hooks/useEnergyDeepDiveTrend';
import CreateFirstDeepdiveCard from './CreateFirstDeepdiveCard';

interface DeepDiveListProps {
  energyGroups: GetEnergyGroupDto[] | undefined;
  accountId: string | undefined;
  savingsByGroupId?: Record<number, number | null>;
  isSavingsLoading?: boolean;
  consumptionYearByGroupId?: Record<number, number | null>;
  isConsumptionYearLoading?: boolean;
  co2eByGroupId?: Record<number, number | null>;
  isCo2eLoading?: boolean;
}

// Format number with Danish locale
const formatConsumption = (value: number | null): string => {
  if (value === null) return 'N/A';
  // Format to 3 significant figures with Danish number format
  const formatted = new Intl.NumberFormat('da-DK', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value / 1000); // Convert from Wh to kWh
  return formatted;
};

// Helper for plural/singular forms
const getPluralizedText = (
  key: string,
  count: number,
  t: (key: string, options?: Record<string, unknown>) => string,
) => {
  // Try plural form first if count > 1, fall back to singular
  if (count !== 1) {
    const pluralKey = `${key}_plural`;
    const pluralText = t(pluralKey, { count });
    // If translation exists and isn't the key itself, use it
    if (pluralText !== pluralKey) return pluralText;
  }
  return t(key, { count });
};

// Custom header with label
const CustomHeaderWithLabel = ({
  label,
  headerName,
}: {
  label: string;
  headerName: string;
}) => (
  <Tooltip title={label} placement="top">
    <Typography
      variant="body2"
      fontWeight="bold"
      sx={{ textAlign: 'center', width: '100%' }}
    >
      {headerName}
    </Typography>
  </Tooltip>
);

const DeepDiveList = ({
  energyGroups,
  accountId,
  savingsByGroupId = {},
  isSavingsLoading = false,
  consumptionYearByGroupId = {},
  isConsumptionYearLoading = false,
  co2eByGroupId = {},
  isCo2eLoading = false,
}: DeepDiveListProps) => {
  const { t } = useTranslation('energy-analysis');
  const navigate = useNavigate();
  const localeText = useDatagridLocaleText();

  const hasService = useHasService('EnergyXplorer');

  if (!hasService) return <EnergyXplorerCard />;

  if (!energyGroups?.length)
    return <CreateFirstDeepdiveCard accountId={accountId} />;

  const handleRowClick = (groupId: number | undefined) => {
    if (!groupId) return;
    navigate({
      to: `/${accountId}/energyxplorer/deepdive/${groupId}/consumption-overview`,
    });
  };

  const handleInitiativesClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    groupId: number | undefined,
  ) => {
    event.stopPropagation();
    if (!groupId) return;

    navigate({
      to: `/${accountId}/energyxplorer/deepdive/${groupId}/energy-initiatives`,
    });
  };

  const handleEsgClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    groupId: number | undefined,
  ) => {
    event.stopPropagation();
    if (!groupId) return;

    navigate({
      to: `/${accountId}/energyxplorer/deepdive/${groupId}/esg`,
    });
  };

  // Build rows with trend data
  const rows = energyGroups.map((group, index) => ({
    id: group.energyGroupId || index,
    group,
    accountId,
  }));

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('portfolio.deepdive_list.column_headers.name'),
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => {
        const group = params.row.group as GetEnergyGroupDto;
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <EnergyGroupTypeIcon type={group.type} />
            <Typography variant="body2" fontWeight={600} noWrap>
              {group.name}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'initiatives',
      headerName: t('portfolio.deepdive_list.column_headers.initiatives'),
      width: 160,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomHeaderWithLabel
          headerName={t('portfolio.deepdive_list.column_headers.initiatives')}
          label=""
        />
      ),
      renderCell: (params: GridRenderCellParams) => {
        const group = params.row.group as GetEnergyGroupDto;
        const initiativeCount = group.energyInitiatives?.length ?? 0;
        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
          >
            <Tooltip
              title={t('portfolio.deepdive_list.column_headers.initiatives')}
            >
              <Button
                size="small"
                variant="text"
                color="inherit"
                onClick={(event) =>
                  handleInitiativesClick(event, group.energyGroupId)
                }
                startIcon={
                  <EnergySavingsLeafRoundedIcon
                    sx={{ fontSize: 14, color: 'success.main' }}
                  />
                }
                data-testid={`open-initiatives-button-${group.energyGroupId}`}
              >
                <Typography variant="caption" color="text.secondary">
                  {getPluralizedText(
                    'portfolio.deepdive_list.initiatives',
                    initiativeCount,
                    t,
                  )}
                </Typography>
              </Button>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'saved',
      headerName: t('portfolio.deepdive_list.column_headers.saved'),
      width: 200,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomHeaderWithLabel
          headerName={t('portfolio.deepdive_list.column_headers.saved')}
          label="kWh"
        />
      ),
      renderCell: (params: GridRenderCellParams) => {
        const group = params.row.group as GetEnergyGroupDto;
        const initiativeCount = group.energyInitiatives?.length ?? 0;
        const groupId = group.energyGroupId;
        const savedValue =
          typeof groupId === 'number' ? savingsByGroupId[groupId] : null;

        if (!initiativeCount) {
          return (
            <Typography variant="caption" color="text.disabled">
              {t('dash', { ns: 'common' }) || '—'}
            </Typography>
          );
        }

        if (isSavingsLoading && savedValue == null) {
          return <CircularProgress size={20} />;
        }

        if (savedValue == null) {
          return (
            <Typography variant="caption" color="text.disabled">
              {t('dash', { ns: 'common' }) || 'N/A'}
            </Typography>
          );
        }

        return (
          <Tooltip title={`${formatNumber(savedValue)} kWh`}>
            <Box textAlign="center">
              <Typography variant="subtitle2">
                {formatNumber(savedValue)} kWh
              </Typography>
              <Typography variant="caption" color="text.secondary"></Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'consumptionYear',
      headerName: t('portfolio.deepdive_list.column_headers.consumption_year'),
      width: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const group = params.row.group as GetEnergyGroupDto;
        const groupId = group.energyGroupId;
        const value =
          typeof groupId === 'number'
            ? consumptionYearByGroupId[groupId]
            : null;
        const unit = group.type?.includes('Water') ? 'm³' : 'kWh';

        if (isConsumptionYearLoading && value == null) {
          return <CircularProgress size={20} />;
        }

        if (value == null) {
          return (
            <Typography variant="caption" color="text.disabled">
              {t('dash', { ns: 'common' }) || 'N/A'}
            </Typography>
          );
        }

        return (
          <Typography variant="subtitle2">
            {formatNumber(value)} {unit}
          </Typography>
        );
      },
    },
    {
      field: 'co2e',
      headerName: t('portfolio.deepdive_list.column_headers.co2e'),
      width: 180,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomHeaderWithLabel
          headerName={t('portfolio.deepdive_list.column_headers.co2e')}
          label={t('portfolio.deepdive_list.co2e_tooltip')}
        />
      ),
      renderCell: (params: GridRenderCellParams) => {
        const group = params.row.group as GetEnergyGroupDto;
        const groupId = group.energyGroupId;
        const co2eKg =
          typeof groupId === 'number' ? co2eByGroupId[groupId] : null;

        if (isCo2eLoading && co2eKg == null) {
          return <CircularProgress size={20} />;
        }

        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={0.5}
          >
            {co2eKg == null ? (
              <Typography variant="caption" color="text.disabled">
                {t('dash', { ns: 'common' }) || 'N/A'}
              </Typography>
            ) : (
              <Typography variant="subtitle2">
                {formatNumber(co2eKg)} {t('deepdive.cards.co2e_year.unit.co2e')}
              </Typography>
            )}
            <Tooltip title={t('portfolio.deepdive_list.open_esg')}>
              <IconButton
                size="small"
                aria-label={t('portfolio.deepdive_list.open_esg')}
                onClick={(event) => handleEsgClick(event, groupId)}
                data-testid={`open-esg-button-${groupId}`}
              >
                <OpenInNewRoundedIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'consumptionAndTrend',
      headerName: t('portfolio.deepdive_list.column_headers.consumption'),
      width: 200,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomHeaderWithLabel
          headerName={t('portfolio.deepdive_list.column_headers.consumption')}
          label="kWh"
        />
      ),
      renderCell: (params: GridRenderCellParams) => {
        const group = params.row.group as GetEnergyGroupDto;
        return (
          <DeepDiveConsumptionAndTrendCell
            group={group}
            accountId={params.row.accountId}
          />
        );
      },
    },
  ];

  return (
    <Box height={'100%'}>
      <DataGridPremium
        rows={rows}
        columns={columns}
        hideFooter
        disableRowSelectionOnClick
        onRowClick={(params) => handleRowClick(params.row.group.energyGroupId)}
        localeText={localeText}
        sx={{
          maxHeight: 800,
          height: '100%',
          '& .MuiDataGrid-row': {
            cursor: rows.length ? 'pointer' : 'default',
          },
        }}
      />
    </Box>
  );
};

// Component to handle consumption and trend cell with loading state
const DeepDiveConsumptionAndTrendCell = ({
  group,
  accountId,
}: {
  group: GetEnergyGroupDto;
  accountId: string | undefined;
}) => {
  const { t } = useTranslation('energy-analysis');
  const { last30DaysTotal, trendPercentage, isLoading } =
    useEnergyDeepDiveTrend(accountId, group);

  if (isLoading) {
    return <CircularProgress size={20} />;
  }

  if (last30DaysTotal !== null) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
        <Tooltip title={`${formatConsumption(last30DaysTotal)} kWh`}>
          <Box textAlign="center">
            <Typography variant="subtitle2">
              {formatConsumption(last30DaysTotal)} kWh
            </Typography>
          </Box>
        </Tooltip>
        {trendPercentage !== null && (
          <Box display="flex" alignItems="center">
            <PercentageTrendChip
              value={trendPercentage}
              tooltip={t('portfolio.deepdive_list.trend_tooltip')}
            />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Typography variant="caption" color="text.disabled">
      {'N/A'}
    </Typography>
  );
};

export default DeepDiveList;
